import React, { useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Easing, StyleSheet, type TextStyle } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import { A } from '@expo/html-elements';
import { theme } from '@garnish/constants';
import { BodyText, webOnlyStyles } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useWebClassName } from '@order/shared/hooks';

import { USABLE_NET_BANNER_HEIGHT } from '../constants';
import { useUsableNet } from '../useUsableNet';
import { useUsableNetScrolling } from '../useUsableNetScrolling';

export const UsableNetBanner = () => {
  const isEnabled = useFeatureFlag('cels-3985-usable-net-enabled');

  const { isUsableNetReady } = useUsableNet();
  const { isScrolled } = useUsableNetScrolling();
  const { formatMessage } = useIntl();

  const shouldRenderBanner = isEnabled && isUsableNetReady;

  // ─── Scroll Animation ──────────────────────────────────────────────────────

  const height = useDerivedValue(() => {
    return withTiming(isScrolled ? 0 : USABLE_NET_BANNER_HEIGHT, ANIMATION);
  });

  const animatedStyle = useAnimatedStyle(() => ({ height: height.value }));

  // ─── Adding Class Name ─────────────────────────────────────────────────────

  const ref = useRef(null);
  useWebClassName(ref, shouldRenderBanner ? UsableNetClass : null);

  // ───────────────────────────────────────────────────────────────────────────

  if (!shouldRenderBanner) return null;

  const bannerStyle = [
    styles.container,
    animatedStyle,
    !isScrolled && styles.bannerBorder,
  ];

  return (
    <Animated.View
      accessibilityRole="complementary"
      accessibilityLabel={formatMessage(messages.a11yLabel)}
      style={bannerStyle}
    >
      <BodyText style={[linkStyle, styles.link]} underline sizeMatch={['12']}>
        <A ref={ref} style={styles.link} href="#" onPress={enableAccessibility}>
          <FormattedMessage {...messages.enableAccessibility} />
        </A>
      </BodyText>
    </Animated.View>
  );
};

const enableAccessibility = () => {
  (window as unknown as UsableNetWindow)?.enableUsableNetAssistive?.();
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'UsableNet Assistive',
    description: 'UsableNet | Assistive Label',
  },
  enableAccessibility: {
    defaultMessage: 'Enable Accessibility',
    description: 'UsableNet | Enable Accessibility',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const linkStyle: TextStyle = webOnlyStyles({ textUnderlineOffset: 3 });
const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    backgroundColor: theme.colors.CREAM,
    paddingHorizontal: theme.spacing['4'],
  },
  bannerBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.DARK_KALE,
  },
  link: {
    color: theme.colors.KALE,
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ANIMATION = { duration: 500, easing: Easing.out(Easing.exp) };
const UsableNetClass = 'UsableNetAssistive';

// ─── Types ───────────────────────────────────────────────────────────────────

type UsableNetWindow = {
  enableUsableNetAssistive: () => void;
  disableUsableNetAssistive: () => void;
};
